import { useIonToast } from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { t } from 'ttag';

import { PrintJobOptions } from '~capacitor-plugins/BadgePrinter';
import { Capacitor } from '~capacitor-plugins/core';
import { Preferences } from '~capacitor-plugins/preferences';

const defaultSettings: PrintJobOptions = {
  printer: undefined,
  numCopies: 1,
};

export const usePrinterSettings = () => {
  const hasPreferences = Capacitor.isPluginAvailable('Preferences');

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['printers', 'settings'],
    queryFn: (): Promise<PrintJobOptions> =>
      Preferences.get({ key: 'badge-printer-settings' }).then(result => {
        const parsed = JSON.parse(result.value || '{}');
        return { ...defaultSettings, ...parsed };
      }),
    enabled: hasPreferences,
  });

  const mutation = useMutation({
    mutationKey: ['printers', 'save-settings'],
    mutationFn: (settings: PrintJobOptions) =>
      Preferences.set({
        key: 'badge-printer-settings',
        value: JSON.stringify(settings),
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['printers', 'settings'], variables);
    },
  });

  const [presentToast] = useIonToast();

  if (!hasPreferences) {
    return {
      isLoading: false,
      settings: defaultSettings,
      saveSettings: () => {
        presentToast({
          message: t`Please update the application to enable badge printing settings`,
          color: 'dark',
        });
      },
    };
  }

  return {
    isLoading: query.isLoading,
    settings: query.data || defaultSettings,
    saveSettings: mutation.mutate,
  };
};
